/**
 * Collections (AR case management) — "who do I chase today?"
 *
 * Mirrors `prototype/sales/collections.html`: the screen is a case-management
 * surface over receivables, NOT a receipts ledger. Each row is a
 * *customer* with open exposure, not a receipt.
 *
 * V1.0.x data we have today:
 *   - `reports.aging.byCustomer` (PR-F) gives us the rows: customer,
 *     outstanding, invoice count, oldest days late.
 *
 * V1.x deferred (not shown until the workflow is real):
 *   - Communication timeline per case (needs notifications + messages).
 *   - "Flint:" AI next-step suggestion (needs OpenRouter + priors).
 *   - Reminder batches + templates (needs notifications guardrails).
 *   - Promised / Resolved views (need a `promise` entity + reminder log).
 *
 * The receipts ledger (S17) lives at `/sales/receipts` — link out from the
 * header. AgingCard was removed; its data feeds these cards instead.
 *
 * @see prototype/sales/collections.html
 * @see prototype/README.md — S15 / `/sales/collections` mapping
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { reports as reportsContract } from "@flint/api-contract";
import {
  ArrowRight,
  Bell,
  CalendarDays,
  Check,
  Clock3,
  Download,
  Eye,
  FileText,
  Flag,
  MessageCircle,
  MessageSquare,
  Plus,
  Search,
  Sparkles,
  TrendingDown,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Alert,
  Card,
  CardBody,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "@flint/ui";
import { ArrowDownUp, Clock, PenLine } from "lucide-react";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

export const Route = createFileRoute("/(workspace)/sales/collections/")({
  component: CollectionsPage,
});

// ── Types kept local to this screen ──────────────────────────────────

type AgingCustomerRow = reportsContract.AgingOutput["byCustomer"][number];

/** Derived case stage. V1.0.x only computes from `oldestDays`; once the
 *  notifications vertical lands we'll fold in reminder history + customer
 *  reply state to produce `Reminded` / `Awaiting reply` etc. */
type Stage = "within-terms" | "overdue" | "late";

interface CaseRow {
  customerId: string;
  customerName: string;
  outstandingPaise: bigint;
  invoiceCount: number;
  oldestDays: number;
  stage: Stage;
  /** The single oldest open invoice — drives the lifecycle timeline. */
  oldestInvoice: {
    id: string;
    docNumber: string;
    /** ISO date, YYYY-MM-DD. */
    issuedOn: string;
    /** ISO date, YYYY-MM-DD. */
    dueOn: string;
  };
  openInvoices: CaseInvoice[];
}

interface CaseInvoice {
  id: string;
  docNumber: string;
  issuedOn: string;
  dueOn: string;
  outstandingPaise: bigint;
  ageDays: number;
}

// ── Page ────────────────────────────────────────────────────────────

type StatusTab = "active" | "promised" | "resolved" | "all";
type StageFilter = "all" | "within-terms" | "overdue" | "late";
type SortKey = "oldest-first" | "largest-first" | "newest-first" | "smallest-first";

function CollectionsPage() {
  const aging = trpc.reports.aging.useQuery({});
  const [search, setSearch] = useState("");
  // "Skip block" dismissal is session-scoped — the hero comes back on
  // next page load. A persistent dismissal would need a snoozed_until
  // column on a per-operator settings table (V1.x).
  const [blockSkipped, setBlockSkipped] = useState(false);
  const [statusTab, setStatusTab] = useState<StatusTab>("active");
  const [stageFilter, setStageFilter] = useState<StageFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("oldest-first");

  // Map the aging rows into case rows + derive the stage band. Pure —
  // the parent can render the empty/loading/error states without a
  // separate fetch.
  const cases = useMemo<CaseRow[]>(() => {
    if (!aging.data) return [];
    return aging.data.byCustomer.map((r: AgingCustomerRow) => ({
      customerId: r.customerId,
      customerName: r.customerName,
      outstandingPaise: BigInt(r.outstandingPaise),
      invoiceCount: r.invoiceCount,
      oldestDays: r.oldestDays,
      stage: stageFromDays(r.oldestDays),
      oldestInvoice: r.oldestInvoice,
      openInvoices: r.openInvoices.map((invoice: AgingCustomerRow["openInvoices"][number]) => ({
        id: invoice.id,
        docNumber: invoice.docNumber,
        issuedOn: invoice.issuedOn,
        dueOn: invoice.dueOn,
        outstandingPaise: BigInt(invoice.outstandingPaise),
        ageDays: invoice.ageDays,
      })),
    }));
  }, [aging.data]);

  const filteredCases = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = cases;
    if (q) rows = rows.filter((c) => c.customerName.toLowerCase().includes(q));
    if (stageFilter !== "all") rows = rows.filter((c) => c.stage === stageFilter);
    // Sort is stable on a fresh copy so the upstream order doesn't leak
    // into a different sort selection later.
    const sorted = [...rows];
    switch (sortKey) {
      case "oldest-first":
        sorted.sort((a, b) => b.oldestDays - a.oldestDays);
        break;
      case "newest-first":
        sorted.sort((a, b) => a.oldestDays - b.oldestDays);
        break;
      case "largest-first":
        sorted.sort((a, b) => (a.outstandingPaise < b.outstandingPaise ? 1 : -1));
        break;
      case "smallest-first":
        sorted.sort((a, b) => (a.outstandingPaise > b.outstandingPaise ? 1 : -1));
        break;
    }
    return sorted;
  }, [cases, search, stageFilter, sortKey]);

  // Page-scoped roll-up — feeds the lead line + KPI tiles.
  const totalOutstanding = aging.data?.totalOutstandingPaise
    ? BigInt(aging.data.totalOutstandingPaise)
    : 0n;
  const customerCount = cases.length;
  const oldestDays = cases.reduce((m, c) => Math.max(m, c.oldestDays), 0);

  // Overdue subset (mirrors prototype's "Total overdue" card). A case is
  // overdue when its oldest open invoice has crossed its due date; the
  // metric sums only the past-due invoices' balances so partials don't
  // get double-counted as fully overdue.
  const overdueCases = useMemo(() => cases.filter((c) => c.oldestDays > 0), [cases]);
  const overduePaise = useMemo(
    () =>
      overdueCases.reduce(
        (sum, c) =>
          sum +
          c.openInvoices
            .filter((inv) => inv.ageDays > 0)
            .reduce((s, inv) => s + inv.outstandingPaise, 0n),
        0n,
      ),
    [overdueCases],
  );
  const overdueCustomerCount = overdueCases.length;
  const oldestOverdueDays = overdueCases.reduce((m, c) => Math.max(m, c.oldestDays), 0);

  // Average days late across overdue cases (the cases that contribute to
  // the overdue tile). Empty when there are no overdue cases.
  const avgDaysLate =
    overdueCases.length === 0
      ? 0
      : Math.round(overdueCases.reduce((s, c) => s + c.oldestDays, 0) / overdueCases.length);
  const recordReceiptInvoiceId = filteredCases[0]?.oldestInvoice.id ?? cases[0]?.oldestInvoice.id;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Sales" }, { label: "Collections" }]}>
      <div className="flex flex-col gap-4">
        {/* ── Page header ─────────────────────────────────────────── */}
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-ink">Collections</h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
              <span>
                <span className="font-mono font-semibold text-ink">
                  {formatLakhCrore(overduePaise)}
                </span>{" "}
                overdue
              </span>
              <Dot />
              <span>
                <span className="font-mono font-semibold text-ink">{customerCount}</span>{" "}
                {customerCount === 1 ? "active case" : "active cases"}
              </span>
              {/* Promises-expected-today lights up once we have a promise
                  entity (V1.x notifications module). Skip the slot until
                  there's something honest to show. */}
              {oldestDays > 0 ? (
                <>
                  <Dot />
                  <span>
                    oldest <span className="font-mono">{oldestDays}</span> days
                  </span>
                </>
              ) : null}
            </p>
          </div>

          {/* Action toolbar — Record receipt (live), Export + Send reminder
              batch (V1.x; surfaced as disabled affordances per AGENTS.md
              §13 V1.x placeholder pattern so the chrome matches the
              prototype today). */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {recordReceiptInvoiceId ? (
              <Link
                to="/sales/invoices/$invoiceId"
                params={{ invoiceId: recordReceiptInvoiceId }}
                className="inline-flex h-9 items-center gap-1.5 rounded-sm bg-ink px-3.5 text-xs font-semibold text-paper transition-colors hover:bg-ink-2"
              >
                <Plus size={13} strokeWidth={2.4} aria-hidden />
                <span>Record receipt</span>
              </Link>
            ) : (
              <button
                type="button"
                disabled
                className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 rounded-sm bg-ink px-3.5 text-xs font-semibold text-paper opacity-60"
              >
                <Plus size={13} strokeWidth={2.4} aria-hidden />
                <span>Record receipt</span>
              </button>
            )}
            <button
              type="button"
              disabled
              title="CSV export lands in V1.x"
              className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 rounded-sm border border-line bg-paper px-3.5 text-xs font-semibold text-ink-2 opacity-60"
            >
              <Download size={13} strokeWidth={2} aria-hidden />
              <span>Export</span>
            </button>
            <button
              type="button"
              disabled
              title="Reminder batches land with the notifications module (V1.x)"
              className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 rounded-sm bg-ink px-3.5 text-xs font-semibold text-paper opacity-60"
            >
              <MessageCircle size={13} strokeWidth={2.4} aria-hidden />
              <span>Send reminder batch</span>
            </button>
          </div>
        </header>

        {/* ── KPI strip ─────────────────────────────────────────────
            Four cards mirror the prototype. Two are live today (Total
            overdue + Avg days late); two are V1.x placeholders (Expected
            this week needs a promise entity; Reminders this month needs
            the notifications-module send log). The placeholder cards
            dim via `deferred` so the layout matches without lying. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            accent="rose"
            icon={Clock3}
            label="Total overdue"
            value={formatLakhCrore(overduePaise)}
            meta={
              overdueCustomerCount === 0
                ? "Nothing past due"
                : `${overdueCustomerCount} ${overdueCustomerCount === 1 ? "customer" : "customers"} · oldest ${oldestOverdueDays}d`
            }
          />
          <KpiTile
            accent="indigo"
            icon={CalendarDays}
            label="Expected this week"
            value="—"
            meta="Promise tracking lands in V1.x"
            deferred
          />
          <KpiTile
            accent="amber"
            icon={MessageSquare}
            label="Reminders this month"
            value="—"
            meta="Send log lands with the notifications module"
            deferred
          />
          <KpiTile
            accent="emerald"
            icon={TrendingDown}
            label="Avg days late"
            value={`${avgDaysLate}`}
            valueSuffix="days"
            meta={
              overdueCases.length === 0
                ? "Nothing past due"
                : `Across ${overdueCases.length} overdue ${overdueCases.length === 1 ? "case" : "cases"}`
            }
          />
        </div>

        {/* ── Hero · Today's collection block ─────────────────────── */}
        {cases.length > 0 && !blockSkipped ? (
          <CollectionsHero
            cases={cases}
            totalOutstanding={totalOutstanding}
            oldestDays={oldestDays}
            onSkipBlock={() => setBlockSkipped(true)}
            ifClearedLabel={
              // Clearing every overdue case zeroes the overdue total. The
              // % drop on overall outstanding is what an operator can act
              // on — "clear all 3 and you're back to within-terms only".
              totalOutstanding > 0n
                ? `−${Math.round((Number(overduePaise) / Number(totalOutstanding)) * 100)}% overdue`
                : "—"
            }
          />
        ) : null}

        {/* ── Status tabs ─────────────────────────────────────────── */}
        <StatusTabBar
          value={statusTab}
          onChange={setStatusTab}
          counts={{
            active: cases.length,
            promised: 0,
            resolved: 0,
            all: cases.length,
          }}
        />

        {/* ── Case list toolbar ──────────────────────────────────
            Search · Stage filter · Sort filter · (spacer) · Reminder
            templates (V1.x) · View receipts ledger.

            Stage + Sort are wired in V1; Reminder templates is a
            V1.x placeholder (needs the notifications/templates module). */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative flex min-w-0 flex-1 sm:flex-initial sm:w-80 items-center">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
              aria-hidden
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer, invoice, GSTIN…"
              aria-label="Search collections cases"
              className="pl-9"
            />
          </label>

          <Select value={stageFilter} onValueChange={(v) => setStageFilter(v as StageFilter)}>
            <SelectTrigger
              aria-label="Filter by stage"
              className={[
                "h-9 w-auto min-w-[8.5rem] rounded-sm border-line bg-paper px-3 text-xs font-semibold text-ink-2",
                stageFilter !== "all" ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "",
              ].join(" ")}
            >
              <Clock size={13} strokeWidth={2} className="mr-1.5" aria-hidden />
              <SelectValue placeholder="Stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Stage · All</SelectItem>
              <SelectItem value="within-terms">Within terms</SelectItem>
              <SelectItem value="overdue">Overdue (31–60d)</SelectItem>
              <SelectItem value="late">Late (60d+)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
            <SelectTrigger
              aria-label="Sort cases"
              className="h-9 w-auto min-w-[10.5rem] rounded-sm border-line bg-paper px-3 text-xs font-semibold text-ink-2"
            >
              <ArrowDownUp size={13} strokeWidth={2} className="mr-1.5" aria-hidden />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="oldest-first">Sort · oldest first</SelectItem>
              <SelectItem value="newest-first">Sort · newest first</SelectItem>
              <SelectItem value="largest-first">Sort · largest first</SelectItem>
              <SelectItem value="smallest-first">Sort · smallest first</SelectItem>
            </SelectContent>
          </Select>

          <span className="hidden flex-1 sm:block" />

          <button
            type="button"
            disabled
            title="Reminder template library lands with the notifications module (V1.x)"
            className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 rounded-sm border border-line bg-paper px-3.5 text-xs font-semibold text-ink-2 opacity-60"
          >
            <PenLine size={13} strokeWidth={2} aria-hidden />
            <span>Reminder templates</span>
          </button>
          <Link
            to="/sales/receipts"
            className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-line bg-paper px-3.5 text-xs font-semibold text-ink-2 transition-colors hover:bg-bg hover:text-ink"
          >
            <span>View receipts ledger</span>
            <ArrowRight size={13} strokeWidth={2.2} aria-hidden />
          </Link>
        </div>

        {/* ── Open cases / tab content ────────────────────────────
            Active + All show the live filtered case list. Promised and
            Resolved both need entities we don't have (promise
            commitments, resolved-case ledger) — render an honest empty
            state so the tab is reachable and the operator understands
            the gap. */}
        {aging.isLoading ? (
          <p className="text-sm text-muted">Loading collections…</p>
        ) : aging.error ? (
          <Alert intent="bad" title="Couldn't load aging" description={aging.error.message} />
        ) : statusTab === "promised" ? (
          <EmptyPane
            icon={CalendarDays}
            title="Promised payments land in V1.x"
            body="Once the notifications module ships, customer commitments captured from WhatsApp replies will show up here so you can chase the ones slipping past their promise date."
          />
        ) : statusTab === "resolved" ? (
          <EmptyPane
            icon={Check}
            title="Resolved cases land in V1.x"
            body="Cases that fully settle (receipt + zero balance) will collect here as a monthly close-out feed."
          />
        ) : filteredCases.length === 0 ? (
          <EmptyPane
            icon={Check}
            title={cases.length === 0 ? "Nothing open right now." : "No cases match your search."}
            body={
              cases.length === 0
                ? "When invoices need collection follow-up, they appear here as cases to review."
                : "Try clearing the search."
            }
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {filteredCases.map((c) => (
              <li key={c.customerId}>
                <CaseCard row={c} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ── Hero · Today's collection block ──────────────────────────────────

/**
 * CollectionsHero — the "block-hero" surface from the prototype.
 *
 * Flint is supposed to stage the operator's day by leverage; the AI
 * vertical (V1.x) will refine this with reply-rate priors. Today the
 * stack order is deterministic: sort by outstanding amount and show
 * the top 3 as coloured chips, with the narrative phrased honestly.
 */
function CollectionsHero({
  cases,
  totalOutstanding,
  oldestDays,
  onSkipBlock,
  ifClearedLabel,
}: {
  cases: CaseRow[];
  totalOutstanding: bigint;
  oldestDays: number;
  /** Optional handler for the "Skip block" button. When omitted the
   *  button doesn't render — keeps the hero embeddable in contexts
   *  where dismissal doesn't make sense. */
  onSkipBlock?: () => void;
  /** Pre-formatted label for the "If you clear all" stat. Computed by
   *  the parent because it depends on data outside the hero's view
   *  (total outstanding incl. non-case rows). */
  ifClearedLabel: string;
}) {
  // Sort a shallow copy by outstandingPaise desc so the hero
  // pills and the "Start with X" CTA match each other regardless of
  // the upstream order. Limit to top 3 per the prototype.
  const top = [...cases]
    .sort((a, b) => (a.outstandingPaise < b.outstandingPaise ? 1 : -1))
    .slice(0, 3);
  const first = top[0];
  const restCount = Math.max(cases.length - top.length, 0);

  // Hero narrative — deterministic. Reads cleanly with 1 / 2-3 / 4+
  // cases. Once the AI vertical lands, the wording becomes "Flint
  // stacked your block by leverage: <first> first because…".
  const narrative =
    cases.length === 1 ? (
      <>
        Just <strong>{first?.customerName ?? ""}</strong> to chase today. Open the customer or
        invoice to record payment activity.
      </>
    ) : restCount === 0 ? (
      <>
        Flint stacked your block by leverage: <strong>{first?.customerName} first</strong> (
        {first?.oldestDays}d, {formatLakhCrore(first?.outstandingPaise ?? 0n)}). Then{" "}
        {top
          .slice(1)
          .map((c) => c.customerName)
          .join(", then ")}
        . Reply-rate priors arrive with the AI vertical (V1.x).
      </>
    ) : (
      <>
        Flint stacked your block by leverage: <strong>{first?.customerName} first</strong> (
        {first?.oldestDays}d, {formatLakhCrore(first?.outstandingPaise ?? 0n)}). Then{" "}
        {top.length - 1} more in the top tier plus {restCount} smaller cases behind.
      </>
    );

  return (
    <section
      aria-label="Today's collection block"
      className="relative overflow-hidden rounded border border-line bg-paper shadow-sm"
    >
      {/* Top accent strip — mirrors prototype `.block-hero::before`. */}
      <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-indigo-500" />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* LEFT · main */}
        <div className="min-w-0 p-6">
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber-100 bg-amber-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-amber-700">
            <span
              aria-hidden
              className="size-1.5 rounded-full bg-amber-500 shadow-[0_0_0_3px_rgba(202,138,4,0.18)]"
            />
            Now · Today's collection block
          </span>
          <h2 className="m-0 text-[clamp(20px,3vw,24px)] font-bold leading-tight -tracking-[0.02em] text-ink">
            {cases.length} {cases.length === 1 ? "customer" : "customers"} to chase ·{" "}
            <span className="text-rose-600">{formatLakhCrore(totalOutstanding)}</span> in play
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted">{narrative}</p>

          {/* Top-3 exposure pills */}
          <div className="mt-3.5 flex flex-wrap gap-1.5">
            {top.map((c) => (
              <HeroPill key={c.customerId} row={c} />
            ))}
          </div>

          {/* CTA — Start with X (primary, deep-link to customer) + Send
              WhatsApp batch (V1.x notifications module, disabled) + Skip
              block (session dismissal handled by parent). */}
          <div className="mt-3.5 flex flex-wrap gap-2">
            {first ? (
              <Link
                to="/sales/customers/$customerId"
                params={{ customerId: first.customerId }}
                className="inline-flex h-9 items-center gap-1.5 rounded-sm bg-ink px-4 text-xs font-semibold text-paper hover:bg-ink-2"
              >
                <ArrowRight size={13} strokeWidth={2.2} />
                <span>Start with {first.customerName}</span>
              </Link>
            ) : null}
            <button
              type="button"
              disabled
              title="Batch sends land with the notifications module (V1.x)"
              className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 rounded-sm border border-line bg-paper px-3.5 text-xs font-semibold text-ink-2 opacity-60"
            >
              <MessageCircle size={13} strokeWidth={2} aria-hidden />
              <span>Send WhatsApp batch</span>
            </button>
            {onSkipBlock ? (
              <button
                type="button"
                onClick={onSkipBlock}
                className="inline-flex h-9 items-center gap-1.5 rounded-sm px-3 text-xs font-semibold text-muted hover:bg-bg hover:text-ink"
              >
                Skip block
              </button>
            ) : null}
          </div>
        </div>

        {/* RIGHT · block stats. Five rows mirror the prototype: total
            exposure (live), estimated time (V1.x time-priors), if-cleared
            impact (computed; assumes clearing all current cases zeroes
            overdue), best send window (V1.x reply-time data), oldest
            (live). The two V1.x rows show "—" with a tooltip so the
            layout matches without making up data. */}
        <div className="flex flex-col gap-1.5 border-t border-line bg-raised p-6 lg:border-l lg:border-t-0">
          <span className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted">
            Block stats
          </span>
          <StatRow k="Total exposure" v={formatLakhCrore(totalOutstanding)} tone="warn" />
          <StatRow
            k="Estimated time"
            v="—"
            hint="Per-customer time priors land with the AI vertical (V1.x)"
            deferred
          />
          <StatRow k="If you clear all" v={ifClearedLabel} tone="good" />
          <StatRow
            k="Best send window"
            v="—"
            hint="Per-customer reply-time data lands with the notifications module (V1.x)"
            deferred
          />
          <StatRow
            k="Oldest"
            v={`${oldestDays}d`}
            {...(oldestDays >= 60 ? { tone: "warn" as const } : {})}
          />
        </div>
      </div>
    </section>
  );
}

function HeroPill({ row }: { row: CaseRow }) {
  const dot =
    row.stage === "late"
      ? "bg-rose-500"
      : row.stage === "overdue"
        ? "bg-amber-500"
        : "bg-indigo-500";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-2.5 py-1 text-xs font-medium text-ink-2">
      <span aria-hidden className={`size-1.5 rounded-full ${dot}`} />
      <span className="font-mono font-semibold text-ink">
        {formatLakhCrore(row.outstandingPaise)}
      </span>
      <span className="text-muted">·</span>
      <span>{row.customerName}</span>
      <span className="text-muted">·</span>
      <span className="font-mono text-muted">{row.oldestDays}d</span>
    </span>
  );
}

function StatRow({
  k,
  v,
  tone,
  hint,
  deferred,
}: {
  k: string;
  v: string;
  tone?: "warn" | "good";
  hint?: string;
  /** Dim the row to signal the metric isn't live yet (V1.x). */
  deferred?: boolean;
}) {
  const tint =
    tone === "warn" ? "text-rose-600" : tone === "good" ? "text-emerald-600" : "text-ink";
  return (
    <div
      className={[
        "flex items-baseline justify-between gap-3 py-1.5",
        deferred ? "opacity-60" : "",
      ].join(" ")}
      title={hint}
    >
      <span className="text-xs text-muted">{k}</span>
      <span className={`font-mono text-sm font-bold tabular-nums ${tint}`}>{v}</span>
    </div>
  );
}

// ── KPI tile ─────────────────────────────────────────────────────────

type Accent = "rose" | "emerald" | "amber" | "indigo";

const ACCENT_BAR: Record<Accent, string> = {
  rose: "bg-rose-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  indigo: "bg-indigo-500",
};

const ACCENT_ICON: Record<Accent, string> = {
  rose: "bg-rose-50 text-rose-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  indigo: "bg-indigo-50 text-indigo-600",
};

function KpiTile({
  accent,
  icon: Icon,
  label,
  value,
  valueSuffix,
  meta,
  deferred,
}: {
  accent: Accent;
  icon: LucideIcon;
  label: string;
  value: string;
  valueSuffix?: string;
  meta: string;
  /** Dim the tile to signal the metric isn't live yet (V1.x). */
  deferred?: boolean;
}) {
  return (
    <Card
      className={[
        "relative min-w-0 overflow-hidden transition-all",
        deferred ? "opacity-70" : "hover:-translate-y-px hover:shadow-md",
      ].join(" ")}
    >
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${ACCENT_BAR[accent]}`} />
      <CardBody className="px-4 py-3.5">
        <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted">
          <p className="min-w-0 truncate">{label}</p>
          <span
            aria-hidden
            className={`grid size-7 shrink-0 place-items-center rounded-md ${ACCENT_ICON[accent]}`}
          >
            <Icon size={14} strokeWidth={2} />
          </span>
        </div>
        <p className="text-xl font-bold leading-tight text-ink">
          {value}
          {valueSuffix ? (
            <span className="ml-1 text-sm font-medium text-muted">{valueSuffix}</span>
          ) : null}
        </p>
        <p className="mt-1 text-xs text-muted truncate">{meta}</p>
      </CardBody>
    </Card>
  );
}

// ── Case card ────────────────────────────────────────────────────────

/** Per-stage accent rail + stage-pill styling. Keep these literal so
 *  Tailwind's JIT can pick the classes up at build time. The
 *  `connector` token feeds the gradient line on the right half of the
 *  lifecycle strip — it ramps from the muted middle to the stage
 *  colour at the active marker. */
const STAGE_STYLES: Record<
  Stage,
  { rail: string; pillWrap: string; rowHover: string; connector: string }
> = {
  "within-terms": {
    rail: "before:bg-sky-500",
    pillWrap: "border-sky-100 bg-sky-50 text-sky-700",
    rowHover: "hover:border-sky-200",
    connector: "to-sky-300",
  },
  overdue: {
    rail: "before:bg-amber-500",
    pillWrap: "border-amber-100 bg-amber-50 text-amber-700",
    rowHover: "hover:border-amber-200",
    connector: "to-amber-300",
  },
  late: {
    rail: "before:bg-rose-500",
    pillWrap: "border-rose-100 bg-rose-50 text-rose-700",
    rowHover: "hover:border-rose-200",
    connector: "to-rose-300",
  },
};

function CaseCard({ row }: { row: CaseRow }) {
  const styles = STAGE_STYLES[row.stage];
  const suggestion = flintSuggestion(row);
  return (
    <Card
      className={[
        "relative overflow-hidden transition-colors",
        // Coloured left rail keyed off the stage band. Mirrors prototype
        // `.case.s-severe` / `.s-warn` / `.s-info`.
        'before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-[""]',
        styles.rail,
        styles.rowHover,
      ].join(" ")}
    >
      {/* ── case-head ──────────────────────────────────────────── */}
      <div className="flex items-start gap-3.5 pl-[18px] pr-4 pt-4 pb-3">
        <Avatar name={row.customerName} />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Link
              to="/sales/customers/$customerId"
              params={{ customerId: row.customerId }}
              className="text-base font-bold -tracking-[0.01em] text-ink hover:underline"
            >
              {row.customerName}
            </Link>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${styles.pillWrap}`}
            >
              <span aria-hidden className="size-1.5 rounded-full bg-current" />
              {stageLabel(row.stage, row.oldestDays)}
            </span>
          </div>
          <p className="text-sm text-muted leading-snug">
            <strong className="font-mono font-bold tabular-nums text-ink">
              {formatLakhCrore(row.outstandingPaise)}
            </strong>{" "}
            outstanding · <span className="font-mono">{row.invoiceCount}</span>{" "}
            {row.invoiceCount === 1 ? "invoice" : "invoices"} · oldest{" "}
            <span className="font-mono">{row.oldestDays}d</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 self-start">
          <Link
            to="/sales/customers/$customerId"
            params={{ customerId: row.customerId }}
            className="inline-flex h-8 items-center gap-1.5 rounded-sm border border-line bg-paper px-3 text-xs font-semibold text-ink-2 hover:bg-bg"
          >
            <Eye size={13} aria-hidden />
            <span>View customer</span>
          </Link>
        </div>
      </div>

      {/* ── case-invoices · open invoice breakdown ──────────────── */}
      <OpenInvoiceList invoices={row.openInvoices} oldestInvoiceId={row.oldestInvoice.id} />

      {/* ── case-timeline · oldest invoice lifecycle ──────────────
       *  Industry-standard B2B AR milestones for the oldest overdue
       *  invoice in the customer case. Every date is real (`issuedOn`
       *  / `dueOn` from the domain) — past-due and escalation are
       *  deterministic offsets off the actual due date, not assumptions. */}
      <LifecycleTimeline
        invoice={row.oldestInvoice}
        invoiceCount={row.openInvoices.length}
        oldestDays={row.oldestDays}
        stage={row.stage}
        connectorClass={styles.connector}
      />

      {/* ── case-next · rule-based suggestion ─────────────────── */}
      <div className="mx-[18px] mb-3.5 flex items-start gap-3 rounded-sm border border-indigo-100 border-l-[3px] border-l-indigo-500 bg-indigo-50 px-3.5 py-3">
        <Sparkles size={14} className="mt-0.5 shrink-0 text-indigo-600" aria-hidden />
        <p className="min-w-0 flex-1 text-xs leading-relaxed text-ink-2">
          <strong className="font-semibold text-ink">Suggested next step:</strong> {suggestion.body}{" "}
          <span className="italic text-muted">{suggestion.note}</span>
        </p>
      </div>
    </Card>
  );
}

// ── Open invoices list ──────────────────────────────────────────────

function OpenInvoiceList({
  invoices,
  oldestInvoiceId,
}: {
  invoices: CaseInvoice[];
  oldestInvoiceId: string;
}) {
  return (
    <div className="border-t border-line px-[18px] py-3.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted">
          Open invoices in this case
        </p>
        <span className="text-xs text-muted">
          {invoices.length} {invoices.length === 1 ? "invoice" : "invoices"}
        </span>
      </div>
      <div className="overflow-hidden rounded-sm border border-line">
        <div className="grid grid-cols-[minmax(0,1.2fr)_92px_92px_72px_96px_auto] gap-2 border-b border-line bg-bg px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted">
          <span>Invoice</span>
          <span>Issued</span>
          <span>Due</span>
          <span className="text-right">Age</span>
          <span className="text-right">Open</span>
          <span className="text-right">Action</span>
        </div>
        <div className="divide-y divide-line">
          {invoices.map((invoice) => {
            const dueDelta = daysFromTodayIso(invoice.dueOn);
            const isOldest = invoice.id === oldestInvoiceId;
            return (
              <div
                key={invoice.id}
                className={[
                  "grid grid-cols-[minmax(0,1.2fr)_92px_92px_72px_96px_auto] items-center gap-2 px-3 py-2.5 text-xs",
                  isOldest ? "bg-rose-50/50" : "bg-paper",
                ].join(" ")}
              >
                <div className="min-w-0">
                  <Link
                    to="/sales/invoices/$invoiceId"
                    params={{ invoiceId: invoice.id }}
                    className="font-mono font-semibold text-indigo-700 hover:underline"
                  >
                    {invoice.docNumber}
                  </Link>
                  {isOldest ? (
                    <span className="ml-2 rounded-full bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700">
                      oldest
                    </span>
                  ) : null}
                </div>
                <span className="text-muted">{formatShortDate(invoice.issuedOn)}</span>
                <span className="text-muted">{formatShortDate(invoice.dueOn)}</span>
                <span
                  className={[
                    "text-right font-mono",
                    dueDelta < 0 ? "text-rose-700" : "text-muted",
                  ].join(" ")}
                >
                  {dueStatusLabel(dueDelta)}
                </span>
                <span className="text-right font-mono font-semibold text-ink">
                  {formatLakhCrore(invoice.outstandingPaise)}
                </span>
                <Link
                  to="/sales/invoices/$invoiceId"
                  params={{ invoiceId: invoice.id }}
                  className="justify-self-end text-xs font-semibold text-indigo-700 hover:underline"
                >
                  Open
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Lifecycle timeline ──────────────────────────────────────────────

/**
 * 5-milestone invoice lifecycle. Industry-standard for B2B AR:
 *
 *   1. Issued       — invoice document created (`issuedOn`)
 *   2. Due          — payment-terms deadline (`dueOn`)
 *   3. Past due     — overdue threshold crossed (`dueOn + 1d`)
 *   4. Escalation   — 31-day mark, matches Flint's 31–60 aging bucket
 *                     (`dueOn + 31d`) — typical phone-call window
 *   5. Action       — today, what the operator should do now
 *
 * Every date is real — derived from the oldest open invoice's actual
 * `issued_on` and `due_on` (`agingByCustomerRow.oldestInvoice`), not
 * from term-day assumptions. Steps that haven't been reached render
 * with an "upcoming" treatment so the strip tells the whole story.
 */

type MilestoneState =
  /** A past milestone — done, emerald check. */
  | "done"
  /** A milestone whose date is today (e.g. Due today). */
  | "current"
  /** A future milestone — muted, dashed. */
  | "upcoming"
  /** The rightmost "Action" step — colour keyed off the case stage. */
  | "now-within"
  | "now-overdue"
  | "now-late";

const MILESTONE_MARKER: Record<MilestoneState, string> = {
  done: "border-emerald-500 bg-emerald-50 text-emerald-600",
  current: "border-amber-500 bg-amber-50 text-amber-700 shadow-[0_0_0_4px_rgba(202,138,4,0.18)]",
  upcoming: "border-dashed border-line bg-paper text-muted",
  "now-within": "border-sky-500 bg-sky-50 text-sky-700 shadow-[0_0_0_4px_rgba(14,165,233,0.14)]",
  "now-overdue":
    "border-amber-500 bg-amber-50 text-amber-700 shadow-[0_0_0_4px_rgba(202,138,4,0.18)]",
  "now-late": "border-rose-500 bg-rose-50 text-rose-700 shadow-[0_0_0_4px_rgba(225,29,72,0.18)]",
};

const MILESTONE_EYEBROW: Record<MilestoneState, string> = {
  done: "text-muted",
  current: "text-amber-700",
  // Slightly de-emphasised vs `done` — the marker's dashed border
  // carries most of the "not-yet-reached" signal, the eyebrow stays
  // muted but a touch lighter.
  upcoming: "text-line-2",
  "now-within": "text-sky-700",
  "now-overdue": "text-amber-700",
  "now-late": "text-rose-700",
};

const MILESTONE_VALUE: Record<MilestoneState, string> = {
  done: "text-ink",
  current: "text-amber-700",
  upcoming: "text-muted",
  "now-within": "text-sky-700",
  "now-overdue": "text-amber-700",
  "now-late": "text-rose-700",
};

interface Milestone {
  state: MilestoneState;
  icon: LucideIcon;
  eyebrow: string;
  /** Relative label — "32d ago" / "Today" / "In 12d". */
  value: string;
  /** Absolute date — "5 Mar 2026". */
  caption: string;
}

function LifecycleTimeline({
  invoice,
  invoiceCount,
  oldestDays,
  stage,
  connectorClass,
}: {
  invoice: CaseRow["oldestInvoice"];
  invoiceCount: number;
  oldestDays: number;
  stage: Stage;
  /** Tailwind class for the stage-tinted end of the connector. */
  connectorClass: string;
}) {
  const milestones = buildMilestones(invoice, oldestDays, stage);
  return (
    <details className="group border-t border-line px-[18px] py-3.5">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-3 text-left marker:hidden">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            Oldest invoice timeline
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted">
            Optional context for{" "}
            <Link
              to="/sales/invoices/$invoiceId"
              params={{ invoiceId: invoice.id }}
              className="font-mono font-semibold text-indigo-700 hover:underline"
            >
              {invoice.docNumber}
            </Link>
            . Use the invoice list above to act on a specific invoice.
          </p>
        </div>
        <span className="mt-0.5 shrink-0 text-xs font-semibold text-indigo-700 group-open:hidden">
          Show
        </span>
        <span className="mt-0.5 hidden shrink-0 text-xs font-semibold text-indigo-700 group-open:inline">
          Hide
        </span>
      </summary>
      <div className="mt-3">
        <p className="mb-3 text-xs text-muted">
          This timeline is only for the oldest of {invoiceCount} open{" "}
          {invoiceCount === 1 ? "invoice" : "invoices"}.
        </p>
        <ol className="relative grid grid-cols-5 gap-1">
          {/* Connector strip — gradient from emerald (issued) through
           *  line (neutral middle) to the stage colour at the action
           *  marker. Sits behind the markers (z-0). */}
          <span
            aria-hidden
            className={`absolute left-[10%] right-[10%] top-[13px] h-[2px] rounded-full bg-gradient-to-r from-emerald-300 via-line ${connectorClass}`}
          />
          {milestones.map((m, i) => (
            <MilestoneCell key={i} milestone={m} />
          ))}
        </ol>
      </div>
    </details>
  );
}

function MilestoneCell({ milestone }: { milestone: Milestone }) {
  const { state, icon: Icon, eyebrow, value, caption } = milestone;
  const isNow = state.startsWith("now-");
  return (
    <li className="relative z-10 flex min-w-0 flex-col items-center text-center">
      <span
        aria-hidden
        className={[
          "grid size-7 place-items-center rounded-full border-2 bg-paper",
          MILESTONE_MARKER[state],
          isNow ? "animate-[pulse_2.6s_ease-in-out_infinite]" : "",
        ].join(" ")}
      >
        <Icon size={12} strokeWidth={2.2} />
      </span>
      <span
        className={`mt-2 text-xs font-semibold uppercase tracking-wider ${MILESTONE_EYEBROW[state]}`}
      >
        {eyebrow}
      </span>
      <span
        className={`mt-0.5 font-mono text-xs font-semibold tabular-nums ${MILESTONE_VALUE[state]}`}
      >
        {value}
      </span>
      <span className="mt-0.5 text-xs leading-snug text-muted">{caption}</span>
    </li>
  );
}

// ── Milestone construction ──────────────────────────────────────────

function buildMilestones(
  invoice: CaseRow["oldestInvoice"],
  oldestDays: number,
  stage: Stage,
): Milestone[] {
  const todayIso = todayDateIso();
  const pastDueIso = addDaysIso(invoice.dueOn, 1);
  // 31d past due lines up with Flint's 31-60 aging bucket, which is
  // the typical "second-reminder + phone-call" window in B2B AR.
  const escalationIso = addDaysIso(invoice.dueOn, 31);
  const dueDays = daysFromTodayIso(invoice.dueOn);

  return [
    {
      state: "done",
      icon: FileText,
      eyebrow: "Issued",
      value: relativeLabel(daysFromTodayIso(invoice.issuedOn)),
      caption: formatAbsoluteDate(invoice.issuedOn),
    },
    {
      state: oldestDays > 0 ? "done" : "current",
      icon: CalendarDays,
      eyebrow: "Due",
      value: relativeLabel(dueDays),
      caption: formatAbsoluteDate(invoice.dueOn),
    },
    {
      state: oldestDays >= 1 ? "done" : "upcoming",
      icon: Clock3,
      eyebrow: "Past due",
      value: relativeLabel(daysFromTodayIso(pastDueIso)),
      caption: formatAbsoluteDate(pastDueIso),
    },
    {
      state: oldestDays >= 31 ? "done" : "upcoming",
      icon: Flag,
      eyebrow: "Escalation",
      value: relativeLabel(daysFromTodayIso(escalationIso)),
      caption: formatAbsoluteDate(escalationIso),
    },
    {
      state: stage === "late" ? "now-late" : stage === "overdue" ? "now-overdue" : "now-within",
      icon: Bell,
      eyebrow: "Action",
      value: "Today",
      caption: formatAbsoluteDate(todayIso),
    },
  ];
}

// ── Date helpers ────────────────────────────────────────────────────

/** Today as YYYY-MM-DD (local date — no time). */
function todayDateIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Add `days` to an ISO date (YYYY-MM-DD) — wrap-safe. */
function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Negative = past, 0 = today, positive = future. */
function daysFromTodayIso(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${iso}T00:00:00`);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

function relativeLabel(daysFromToday: number): string {
  if (daysFromToday === 0) return "Today";
  if (daysFromToday > 0) return `In ${daysFromToday}d`;
  return `${Math.abs(daysFromToday)}d ago`;
}

function formatAbsoluteDate(iso: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(`${iso}T00:00:00`));
}

function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
  }).format(new Date(`${iso}T00:00:00`));
}

function dueStatusLabel(daysFromToday: number): string {
  if (daysFromToday < 0) return `${Math.abs(daysFromToday)}d late`;
  if (daysFromToday === 0) return "Today";
  return `In ${daysFromToday}d`;
}

/** Deterministic Flint suggestion derived from `oldestDays`. The real
 *  AI vertical (V1.x) will refine this with reply-rate priors per
 *  customer; today the copy is honest about its rules-based origin. */
function flintSuggestion(row: CaseRow): {
  body: React.ReactNode;
  note: string;
} {
  if (row.stage === "late") {
    return {
      body: (
        <>
          {row.customerName} is <strong>{row.oldestDays} days late</strong> — past the
          polite-reminder window. Recommend a direct phone call to ask for a commit date.
        </>
      ),
      note: "Record the call or receipt from the customer/invoice screen.",
    };
  }
  if (row.stage === "overdue") {
    return {
      body: (
        <>
          {row.customerName} is <strong>{row.oldestDays}d past terms</strong>. A polite WhatsApp
          reminder usually clears a case at this stage.
        </>
      ),
      note: "Messaging actions will appear here after the notifications module is live.",
    };
  }
  return {
    body: (
      <>
        {row.customerName} is approaching due ({row.oldestDays}d). A gentle nudge here keeps you
        ahead of the slip.
      </>
    ),
    note: "Use the invoice list above to decide whether to wait or follow up.",
  };
}

// ── Avatar + empty pane + helpers ────────────────────────────────────

/** Per-customer hashed avatar colors — mirrors the prototype's
 *  `.c-acme` / `.c-bharat` / `.c-kothari` / `.c-westline` slots. The
 *  hash is intentionally deterministic so the same customer always
 *  gets the same chip color across reloads. */
const AVATAR_PALETTE = [
  "bg-indigo-600",
  "bg-rose-600",
  "bg-emerald-600",
  "bg-amber-600",
  "bg-sky-600",
  "bg-violet-600",
] as const;

function avatarColorClass(customerKey: string): string {
  let h = 0;
  for (let i = 0; i < customerKey.length; i++) {
    h = (h * 31 + customerKey.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length] ?? AVATAR_PALETTE[0]!;
}

function Avatar({ name }: { name: string }) {
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "–";
  return (
    <span
      aria-hidden
      className={`grid size-11 shrink-0 place-items-center rounded-sm text-sm font-bold text-white ${avatarColorClass(name)}`}
    >
      {initials}
    </span>
  );
}

function EmptyPane({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <Card>
      <CardBody className="flex flex-col items-center gap-2 py-10 text-center">
        <span
          aria-hidden
          className="grid size-10 place-items-center rounded-full bg-raised text-muted"
        >
          <Icon size={18} />
        </span>
        <p className="text-base font-medium text-ink">{title}</p>
        <p className="max-w-md text-sm text-muted">{body}</p>
      </CardBody>
    </Card>
  );
}

function Dot() {
  return <span aria-hidden className="inline-block size-1 rounded-full bg-line-2" />;
}

/**
 * StatusTabBar — Active / Promised / Resolved · this month / All.
 *
 * Active = currently-open cases (drives the default view). Promised +
 * Resolved are V1.x destinations (need a `promise` entity and a
 * resolved-cases ledger). They render with count `0` and the tab pane
 * shows an empty-state explaining the gap.
 */
function StatusTabBar({
  value,
  onChange,
  counts,
}: {
  value: StatusTab;
  onChange: (next: StatusTab) => void;
  counts: Record<StatusTab, number>;
}) {
  const TABS: ReadonlyArray<{ value: StatusTab; label: string }> = [
    { value: "active", label: "Active" },
    { value: "promised", label: "Promised" },
    { value: "resolved", label: "Resolved · this month" },
    { value: "all", label: "All" },
  ];
  return (
    <TabsRoot value={value} onValueChange={(v) => onChange(v as StatusTab)}>
      <TabsList aria-label="Collections status">
        {TABS.map((t) => (
          <TabsTrigger key={t.value} value={t.value} count={counts[t.value]}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </TabsRoot>
  );
}

// ── Pure helpers ─────────────────────────────────────────────────────

function stageFromDays(oldestDays: number): Stage {
  if (oldestDays >= 60) return "late";
  if (oldestDays >= 31) return "overdue";
  return "within-terms";
}

function stageLabel(stage: Stage, oldestDays: number): string {
  switch (stage) {
    case "within-terms":
      return `Within terms · ${oldestDays}d`;
    case "overdue":
      return `Overdue · ${oldestDays}d`;
    case "late":
      return `Late · ${oldestDays}d overdue`;
  }
}

/** Format paise as "₹X.XL" / "₹X.XCr" for the compact KPI/case display.
 *  Same lakh/crore convention as the invoice list KPI strip. */
function formatLakhCrore(paise: bigint): string {
  if (paise === 0n) return "₹0";
  const sign = paise < 0n ? "-" : "";
  const abs = paise < 0n ? -paise : paise;
  // Thresholds in paise: ₹1 Cr = 10_000_000 rupees = 1_000_000_000 paise.
  // 1 lakh = 100_000 rupees = 10_000_000 paise.
  if (abs >= 1_000_000_000n /* ₹1 Cr */) {
    const crores = Number(abs / 10_000_000n) / 100; // 2 dp
    return `${sign}₹${crores.toFixed(2)}Cr`;
  }
  if (abs >= 10_000_000n /* ₹1 L */) {
    const lakhs = Number(abs / 100_000n) / 100; // 2 dp on lakhs
    return `${sign}₹${lakhs.toFixed(1)}L`;
  }
  // < 1 lakh — show plain rupees with grouping (paise dropped).
  const rupees = abs / 100n;
  return `${sign}₹${rupees.toLocaleString("en-IN")}`;
}
